import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApiMutation } from "../hooks/useApi";
import { SystemUser } from "../types/contact";

interface Props {
  handleUnlockOpen: (item: SystemUser) => void;
  handleUnlockClose: () => void;
  setUnlockOpen: React.Dispatch<React.SetStateAction<boolean>>;
  unlockOpen: boolean;
  selectedItem: SystemUser | null;
  handlePromoteOpen: (item: SystemUser) => void;
  promoted: boolean;
  setPromoted: React.Dispatch<React.SetStateAction<boolean>>;
}

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const UnlockDialog: FunctionComponent<Props> = (props: Props): JSX.Element => {
  const {
    handleUnlockOpen,
    handleUnlockClose,
    unlockOpen,
    setUnlockOpen,
    selectedItem,
    handlePromoteOpen,
    promoted,
    setPromoted,
  } = props;
  const [timesheetAccess, setTimesheetAccess] = useState<string>(
    selectedItem?.accessLevel === 1 ? "Own" : "All"
  );
  const [switchChecked, setSwitchChecked] = useState<boolean | undefined>(
    !!selectedItem?.isLocked
  );
  const { apiUpdate } = useApiMutation();

  const { t } = useTranslation();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (selectedItem) {
      setSwitchChecked(!!selectedItem.isLocked);
      setTimesheetAccess(selectedItem?.accessLevel === 1 ? "Own" : "All");
    }
  }, [selectedItem]);

  const handleChange = (event: SelectChangeEvent) => {
    setTimesheetAccess(event.target.value);
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSwitchChecked(!switchChecked);
  };

  const handleSave = (selectedItem: SystemUser | null) => {
    apiUpdate.mutate(
      {
        entity: "systemusers",
        post: {
          id: selectedItem?.id,
          uid: selectedItem?.uid,
          isLocked: switchChecked,
          accessLevel: timesheetAccess === "Own" ? 1 : 2,
        },
      },
      {
        onSuccess: () => {
          handleUnlockClose();
          setPromoted(false);
        },
        onError: (error) => console.error(error),
      }
    );
  };

  return (
    <BootstrapDialog
      onClose={handleUnlockClose}
      aria-labelledby="customized-dialog-title"
      open={unlockOpen}
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>{selectedItem?.name}</DialogTitle>

      <IconButton
        aria-label="close"
        onClick={handleUnlockClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <FormControl
          sx={{ m: 1, minWidth: isSmallScreen ? "" : "500px" }}
          disabled={selectedItem?.isPrimary ? true : false}
        >
          <InputLabel> {t("adminTable.timesheetAccess")} </InputLabel>
          <Select
            size={isSmallScreen ? "small" : "medium"}
            value={timesheetAccess}
            label={t("adminTable.timesheetAccess")}
            onChange={handleChange}
          >
            <MenuItem value="All">
              {t("dialogs.adminDialog.allTimesheets")}
            </MenuItem>
            <MenuItem value="Own">
              {t("dialogs.adminDialog.ownTimesheets")}
            </MenuItem>
          </Select>
          <FormHelperText> {t("dialogs.adminDialog.helper")} </FormHelperText>
        </FormControl>

        <FormControl
          sx={{
            m: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "fit-content",
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={switchChecked}
                onChange={handleSwitchChange}
                color="error"
                size={isSmallScreen ? "small" : "medium"}
              />
            }
            label={
              switchChecked ? t("adminTable.locked") : t("adminTable.active")
            }
          />
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button
          autoFocus
          onClick={() => {
            // handleSave(selectedItem)
            selectedItem && handlePromoteOpen(selectedItem);
          }}
          disabled={selectedItem?.isPrimary || promoted}
        >
          {t("buttons.promote")}
        </Button>
        <Button variant="contained" onClick={() => handleSave(selectedItem)}>
          {t("buttons.save")}
        </Button>
      </DialogActions>
    </BootstrapDialog>
  );
};

export default UnlockDialog;
